/**
 * MIXED MODULE - mostly SAMPLE DATA, plus a few real app-chrome constants.
 *
 * SAMPLE (hard-coded placeholders from the original dashboard template, not
 * drawn from the CRM; no backing source exists for marketing spend, revenue vs
 * target, attribution or AI insights): metrics, leadsOverTime, leadsSummary,
 * attribution, insights, revenueTarget, marketingGoals, pipelineStages,
 * leaderboard.
 *   - components/dashboard/metric-cards.tsx        (metrics, as default prop)
 *   - components/dashboard/leads-over-time.tsx     (leadsOverTime, leadsSummary)
 *   - components/dashboard/attribution-breakdown.tsx (attribution)
 *   - components/dashboard/sales-leaderboard.tsx   (leaderboard, when no live reps)
 *   - components/dashboard/{ai-insights,marketing-goals,pipeline-by-stage,
 *     revenue-vs-target}.tsx - always render these; currently not mounted on any route
 * Each shows a <SampleDataBadge /> whenever it renders these values.
 *
 * NOT sample (navigation / identity config, leave unmarked): navItems,
 * workspace, user, crmDemoItems - consumed by app-sidebar, site-header and
 * settings/profile-form.
 */

export const metrics = [
  { title: "Marketing spend", value: "$84,300", delta: "+8.2%", tone: "success", note: "from last month" },
  { title: "Total revenue", value: "$1,250.00", delta: "+12.5%", tone: "success", note: "from last month" },
  { title: "Cost per lead", value: "$29.61", delta: "+12.5%", tone: "destructive", note: "from last month" },
  { title: "Lead conversion rate", value: "3.4%", delta: "+12.5%", tone: "success", note: "from last month" },
] as const

// 32 daily points, Jul 1 → Aug 1. Values are on a 0–120 scale.
const previousNorm = [0.525,0.542,0.558,0.550,0.583,0.592,0.625,0.633,0.625,0.600,0.617,0.600,0.583,0.592,0.617,0.625,0.633,0.667,0.667,0.667,0.692,0.717,0.758,0.742,0.767,0.733,0.775,0.767,0.767,0.767,0.758,0.750]
const currentNorm  = [0.583,0.592,0.625,0.633,0.642,0.667,0.692,0.692,0.692,0.667,0.683,0.667,0.667,0.667,0.675,0.717,0.733,0.742,0.758,0.758,0.783,0.817,0.833,0.858,0.867,0.842,0.867,0.875,0.867,0.850,0.867,0.850]

export const leadsOverTime = previousNorm.map((p, i) => {
  const d = new Date(Date.UTC(2024, 6, 1 + i))
  return {
    date: d.toISOString().slice(0, 10),
    previous: Math.round(p * 120),
    current: Math.round(currentNorm[i] * 120),
  }
})

export const leadsSummary = { total: "2847", delta: "+12.5%", note: "vs previous period" }

export const attribution = [
  { channel: "Organic", firstTouch: 342, lastTouch: 262 },
  { channel: "Paid", firstTouch: 306, lastTouch: 245 },
  { channel: "Socials", firstTouch: 198, lastTouch: 107 },
  { channel: "Direct", firstTouch: 214, lastTouch: 398 },
  { channel: "Other", firstTouch: 181, lastTouch: 229 },
]

export const insights = [
  { icon: "trending-up", title: "Win rate up 4 pts", description: "32% this quarter, driven by faster Proposal-stage exits." },
  { icon: "triangle-alert", title: "Cost per lead climbing", description: "CPL rose 12.5% while spend grew 8.2%. Socials is the driver." },
  { icon: "target", title: "Q3 forecast gap", description: "Forecast $1.38M against the $1.5M target, an 8% gap." },
  { icon: "sparkles", title: "Organic outperforming paid", description: "Organic drives the most first-touch revenue this month." },
] as const

export const revenueTarget = {
  closed: 412_000,
  target: 1_500_000,
  forecast: 1_380_000,
  ticks: 40,
}

export const marketingGoals = [
  { label: "Pipeline created", current: 12_847, target: 13_000, format: "currency" },
  { label: "Leads generated", current: 2_830, target: 3_000, format: "number" },
  { label: "New customers", current: 459, target: 600, format: "number" },
] as const

export const pipelineStages = [
  { stage: "Lead", value: 763_000, share: 38 },
  { stage: "Qualified", value: 482_000, share: 24 },
  { stage: "Proposal", value: 361_000, share: 18 },
  { stage: "Negotiation", value: 241_000, share: 12 },
  { stage: "Closed won", value: 161_000, share: 8 },
]

export const leaderboard = [
  { rank: 1, name: "Noah Mark Davis", avatar: "https://assets.shadcncraft.com/registry/avatars/person-7.webp", attained: 410, quota: 250 },
  { rank: 2, name: "Ethan Parker Jenson", avatar: "https://assets.shadcncraft.com/registry/avatars/person-1.webp", attained: 220, quota: 200 },
  { rank: 3, name: "Sophia Brown", avatar: "https://assets.shadcncraft.com/registry/avatars/person-5.webp", attained: 230, quota: 230 },
  { rank: 4, name: "Liam Smith", avatar: "https://assets.shadcncraft.com/registry/avatars/person-4.webp", attained: 160, quota: 210 },
  { rank: 5, name: "Olivia Johnson", avatar: "https://assets.shadcncraft.com/registry/avatars/person-2.webp", attained: 135, quota: 220 },
]

export const navItems = [
  { title: "Dashboard", href: "/", icon: "layout-dashboard" },
  { title: "Tasks", href: "/tasks", icon: "list-todo" },
  { title: "Deals", href: "/deals", icon: "square-kanban" },
  { title: "Customers", href: "/customers", icon: "users" },
  { title: "Contacts", href: "/contacts", icon: "contact" },
  { title: "Schedule", href: "/schedule", icon: "calendar-days" },
  { title: "Activity", href: "/activity", icon: "activity" },
  { title: "Inventory", href: "/inventory", icon: "package" },
  { title: "Campaigns", href: "/campaigns", icon: "megaphone" },
  { title: "Competitors", href: "/competitors", icon: "swords" },
  { title: "Analytics", href: "/analytics", icon: "chart-line" },
  { title: "Settings", href: "/settings", icon: "settings" },
] as const

export const user = {
  name: "Mia Ward",
  email: "mia@veez.io",
  avatar: "https://assets.shadcncraft.com/registry/avatars/person-3.webp",
}

export const workspace = { name: "Veez", plan: "Pro Application" }

// Standalone ReUI CRM clones (reui.io/blocks/solutions/crm). Separate pages
// that reuse the dashboard shell but leave the existing routes untouched.
export const crmDemoItems = [
  { title: "CRM 1 · Dashboard", href: "/crm-1", icon: "layout-dashboard" },
  { title: "CRM 2 · Board", href: "/crm-2", icon: "square-kanban" },
  { title: "CRM 3 · Contacts", href: "/crm-3", icon: "users" },
  { title: "CRM 4 · Deal Detail", href: "/crm-4", icon: "file-text" },
  { title: "CRM 5 · Activity", href: "/crm-5", icon: "activity" },
  { title: "CRM 6 · Import", href: "/crm-6", icon: "upload" },
  { title: "CRM 7 · Schedule", href: "/crm-7", icon: "calendar-days" },
  { title: "CRM 8 · Workflow Board", href: "/crm-8", icon: "square-kanban" },
  { title: "CRM 9 · Profile Form", href: "/crm-9", icon: "id-card" },
  { title: "Account Details", href: "/account-details", icon: "building-2" },
] as const
